import { Component, input, output, OnInit, OnDestroy } from '@angular/core';
import { NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { CartService } from '../../services/cart.service';
import { ShopifyProduct } from '../models';
import { Subscription } from 'rxjs';

interface MagnetDesign {
  id: string;
  thumbnailUrl: string;
  quantity: number;
}

@Component({
    selector: 'app-purchase-controls',
    templateUrl: './purchase-controls.component.html',
    styleUrls: ['./purchase-controls.component.scss'],
    imports: [NgClass, TranslateModule],
})
export class PurchaseControlsComponent implements OnInit, OnDestroy {
  quantity = input(1);
  isFinishedEditing = input(false);
  isProcessingOrder = input(false);
  price = input<any>();
  valentinesBundle = input<{ singlePrice: number; bundlePrice: number } | null>(null);
  hideQuantityControls = input(false);
  designs = input<MagnetDesign[]>([]);
  allDesignsHaveLocation = input(false);

  buyNow = output<void>();
  quantityChange = output<number>();
  updateDesignQuantity = output<{id: string, quantity: number}>();
  removeDesign = output<string>();
  editDesign = output<string>();
  addAnotherDesign = output<void>();
  openAllMagnets = output<void>();

  isCartExpanded: boolean = false;
  showDeleteConfirmation: boolean = false;
  showEditConfirmation: boolean = false;
  pendingActionDesignId: string | null = null;
  pendingActionProductId: string | null = null;
  showDeleteProductConfirmation: boolean = false;

  readonly MIN_QUANTITY: number = 1;
  readonly MAX_QUANTITY: number = 10;

  shopifyProducts: Array<{ product: ShopifyProduct; quantity: number }> = [];
  private subscriptions: Subscription[] = [];

  constructor(private cartService: CartService) {}

  // TODO: Bundle pricing logic - to be implemented later
  // // Hardcoded bundle pricing rules
  // // Format: { quantity: totalPrice }
  // private bundleRules: { [key: number]: number } = {
  //   1: 25,   // 1 item: 25 euro
  //   3: 60,   // 3 items: 60 euro (20 per item)
  //   5: 75    // 5 items: 75 euro (15 per item)
  // };

  // Get base price per unit from Shopify
  get basePricePerUnit(): number {
    const p = this.price();
    return p?.price?.amount ? parseFloat(p.price.amount) : 0;
  }

  // Get compare at price per unit from Shopify (original price before discount)
  get compareAtPricePerUnit(): number {
    const p = this.price();
    return p?.compareAtPrice?.amount ? parseFloat(p.compareAtPrice.amount) : 0;
  }

  // Check if there's a compare at price (discount available) for custom magnets or any Shopify product
  get hasCompareAtPrice(): boolean {
    if (this.valentinesBundle() && this.quantity() >= 2) {
      return true;
    }
    if (this.compareAtPricePerUnit > 0 && this.compareAtPricePerUnit > this.basePricePerUnit) {
      return true;
    }
    return this.shopifyProducts.some(
      item => item.product.compareAtPrice?.amount != null &&
        parseFloat(item.product.compareAtPrice!.amount) > parseFloat(item.product.price.amount)
    );
  }

  // Get currency code from Shopify
  get currency(): string {
    const p = this.price();
    return p?.price?.currencyCode || 'EUR';
  }

  // TODO: Bundle pricing logic - to be implemented later
  // // Calculate bundle price based on quantity
  // getBundlePrice(quantity: number): number {
  //   // Check if there's an exact bundle match
  //   if (this.bundleRules[quantity]) {
  //     return this.bundleRules[quantity];
  //   }

  //   // Find the best bundle combination
  //   // For now, use the largest bundle that fits, then add remaining at base price
  //   const bundleQuantities = Object.keys(this.bundleRules)
  //     .map(q => parseInt(q))
  //     .sort((a, b) => b - a); // Sort descending

  //   let totalPrice = 0;
  //   let remainingQuantity = quantity;

  //   // Apply largest bundles first
  //   for (const bundleQty of bundleQuantities) {
  //     const bundles = Math.floor(remainingQuantity / bundleQty);
  //     if (bundles > 0) {
  //       totalPrice += bundles * this.bundleRules[bundleQty];
  //       remainingQuantity -= bundles * bundleQty;
  //     }
  //   }

  //   // If there's remaining quantity, use base price
  //   if (remainingQuantity > 0) {
  //     totalPrice += remainingQuantity * this.basePrice;
  //   }

  //   return totalPrice;
  // }

  // Get total price of all Shopify products in cart (discounted/current price)
  get shopifyProductsTotal(): number {
    return this.shopifyProducts.reduce((sum, item) => {
      return sum + parseFloat(item.product.price.amount) * item.quantity;
    }, 0);
  }

  // Get total compare-at price of all Shopify products (original price before discount)
  get shopifyProductsCompareAtTotal(): number {
    return this.shopifyProducts.reduce((sum, item) => {
      const compareAt = item.product.compareAtPrice?.amount;
      const original = compareAt && parseFloat(compareAt) > parseFloat(item.product.price.amount)
        ? parseFloat(compareAt)
        : parseFloat(item.product.price.amount);
      return sum + original * item.quantity;
    }, 0);
  }

  // Get current total price based on quantity (simple calculation or Valentine's bundle)
  get currentTotalPrice(): number {
    let customTotal: number;
    const vb = this.valentinesBundle();
    const qty = this.quantity();
    if (vb) {
      const bundles = Math.floor(qty / 2);
      const singles = qty % 2;
      customTotal = bundles * vb.bundlePrice + singles * vb.singlePrice;
    } else {
      customTotal = qty * this.basePricePerUnit;
    }
    return customTotal + this.shopifyProductsTotal;
  }

  // Get total compare at price (original price before discount)
  get currentCompareAtPrice(): number {
    let customCompare: number;
    const vb = this.valentinesBundle();
    const qty = this.quantity();
    if (vb && qty >= 2) {
      customCompare = qty * vb.singlePrice;
    } else {
      customCompare = qty * this.compareAtPricePerUnit;
    }
    return customCompare + this.shopifyProductsCompareAtTotal;
  }

  // Get total savings amount
  get totalSavings(): number {
    return this.hasCompareAtPrice ? this.currentCompareAtPrice - this.currentTotalPrice : 0;
  }

  // Get savings percentage
  get savingsPercentage(): number {
    if (!this.hasCompareAtPrice || this.currentCompareAtPrice === 0) return 0;
    return Math.round((this.totalSavings / this.currentCompareAtPrice) * 100);
  }

  // TODO: Bundle pricing logic - to be implemented later
  // // Get price per item (for display)
  // get pricePerItem(): number {
  //   return this.currentTotalPrice / this.quantity;
  // }

  // // Get regular price (without bundle discount)
  // get regularPrice(): number {
  //   return this.basePrice * this.quantity;
  // }

  // // Get savings amount (compared to base price)
  // get savingsAmount(): number {
  //   const regular = this.regularPrice;
  //   const bundle = this.currentTotalPrice;
  //   return regular - bundle;
  // }

  // // Check if current quantity qualifies for a bundle discount
  // get hasBundleDiscount(): boolean {
  //   return this.bundleRules[this.quantity] !== undefined || this.savingsAmount > 0;
  // }

  // // Get savings percentage
  // get savingsPercentage(): number {
  //   if (this.regularPrice === 0) return 0;
  //   return Math.round((this.savingsAmount / this.regularPrice) * 100);
  // }

  // // Get available bundle options for display
  // get bundleOptions(): Array<{ quantity: number; totalPrice: number; perItem: number; savings: number; savingsPercent: number; badge?: string }> {
  //   return Object.keys(this.bundleRules).map(qty => {
  //     const quantity = parseInt(qty);
  //     const totalPrice = this.bundleRules[quantity];
  //     const perItem = totalPrice / quantity;
  //     const regularPrice = this.basePrice * quantity;
  //     const savings = regularPrice - totalPrice;
  //     const savingsPercent = Math.round((savings / regularPrice) * 100);
      
  //     let badge: string | undefined;
  //     if (quantity === 5) {
  //       badge = 'BEST_VALUE';
  //     } else if (quantity === 3) {
  //       badge = 'POPULAR';
  //     }

  //     return {
  //       quantity,
  //       totalPrice,
  //       perItem,
  //       savings,
  //       savingsPercent,
  //       badge
  //     };
  //   }).sort((a, b) => a.quantity - b.quantity);
  // }

  // // Check if a quantity is a bundle option
  // isBundleOption(quantity: number): boolean {
  //   return this.bundleRules[quantity] !== undefined;
  // }

  // // Get savings for a specific bundle quantity
  // getBundleSavings(quantity: number): { amount: number; percent: number } {
  //   if (!this.bundleRules[quantity]) {
  //     return { amount: 0, percent: 0 };
  //   }
  //   const regularPrice = this.basePrice * quantity;
  //   const bundlePrice = this.bundleRules[quantity];
  //   const savings = regularPrice - bundlePrice;
  //   const percent = Math.round((savings / regularPrice) * 100);
  //   return { amount: savings, percent };
  // }

  // // Select a bundle option
  // selectBundle(quantity: number): void {
  //   if (!this.isFinishedEditing) return;
  //   this.quantityChange.emit(quantity);
  // }

  // // Check if a bundle option is currently selected
  // isSelected(quantity: number): boolean {
  //   return this.quantity === quantity;
  // }

  decreaseQuantity(): void {
    if (!this.isFinishedEditing()) return;
    if (this.quantity() > this.MIN_QUANTITY) {
      this.quantityChange.emit(this.quantity() - 1);
    }
  }

  increaseQuantity(): void {
    if (!this.isFinishedEditing()) return;
    if (this.quantity() < this.MAX_QUANTITY) {
      this.quantityChange.emit(this.quantity() + 1);
    }
  }

  onBuyNow(): void {
    this.buyNow.emit();
  }

  toggleCartExpanded(): void {
    this.isCartExpanded = !this.isCartExpanded;
  }

  onUpdateDesignQuantity(id: string, quantity: number): void {
    this.updateDesignQuantity.emit({ id, quantity });
  }

  onRemoveDesign(id: string): void {
    this.pendingActionDesignId = id;
    this.showDeleteConfirmation = true;
  }

  confirmRemoveDesign(): void {
    if (this.pendingActionDesignId) {
      this.removeDesign.emit(this.pendingActionDesignId);
      this.cancelDeleteConfirmation();
    }
  }

  cancelDeleteConfirmation(): void {
    this.showDeleteConfirmation = false;
    this.pendingActionDesignId = null;
  }

  onEditDesign(id: string): void {
    this.pendingActionDesignId = id;
    this.showEditConfirmation = true;
  }

  confirmEditDesign(): void {
    if (this.pendingActionDesignId) {
      this.editDesign.emit(this.pendingActionDesignId);
      this.cancelEditConfirmation();
    }
  }

  cancelEditConfirmation(): void {
    this.showEditConfirmation = false;
    this.pendingActionDesignId = null;
  }

  onAddAnotherDesign(): void {
    this.addAnotherDesign.emit();
  }

  /** Close the cart modal without navigating (used by the modal's own Continue button). */
  onContinueShopping(): void {
    this.toggleCartExpanded();
  }

  /** Navigate to all-magnets from the always-visible browse button (closes cart if open). */
  onBrowseAllMagnets(): void {
    if (this.isCartExpanded) {
      this.isCartExpanded = false;
    }
    this.openAllMagnets.emit();
  }

  ngOnInit(): void {
    // Subscribe to Shopify products changes
    const sub = this.cartService.shopifyProducts$.subscribe(productsMap => {
      this.shopifyProducts = Array.from(productsMap.values());
    });
    this.subscriptions.push(sub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  // Shopify Product Methods
  onRemoveShopifyProduct(productId: string): void {
    this.pendingActionProductId = productId;
    this.showDeleteProductConfirmation = true;
  }

  confirmRemoveShopifyProduct(): void {
    if (this.pendingActionProductId) {
      this.cartService.removeShopifyProduct(this.pendingActionProductId);
      this.cancelDeleteProductConfirmation();
    }
  }

  cancelDeleteProductConfirmation(): void {
    this.showDeleteProductConfirmation = false;
    this.pendingActionProductId = null;
  }

  onUpdateShopifyProductQuantity(productId: string, quantity: number): void {
    this.cartService.updateShopifyProductQuantity(productId, quantity);
  }

  formatProductPrice(amount: string, currencyCode: string): string {
    const price = parseFloat(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode
    }).format(price);
  }

  get hasShopifyProducts(): boolean {
    return this.shopifyProducts.length > 0;
  }

  get totalShopifyProductsQuantity(): number {
    return this.shopifyProducts.reduce((sum, item) => sum + item.quantity, 0);
  }

  /** Total number of items in cart (custom designs + Shopify products) for the cart badge. */
  get totalCartItemCount(): number {
    const customTotal = this.designs().reduce((sum, d) => sum + d.quantity, 0);
    return customTotal + this.totalShopifyProductsQuantity;
  }
}
